import { mapKeys, omit } from 'lodash';
import PropTypes from 'prop-types';
import React, { ComponentType } from 'react';
import { RouteComponentProps, withRouter } from 'react-router';
import {
    ATTRIBUTES_MAP,
    DETAILS_MAP,
    DEFAULT_SOURCE,
} from '../../shared/data/analytics';
import { getViewabilityEvent } from '../utilities/getViewabilityEvent';
import { useStateContext } from '../modules/stateContext';
import { resolvePageForEvent } from '../../shared/utilities/events';

const eventAttributesShape = PropTypes.shape({
    createEventAttributes: PropTypes.func.isRequired,
    createEventHandler: PropTypes.func.isRequired,
});

function getDisplayName(
    WrappedComponent: Partial<Record<'displayName' | 'name', string>>
) {
    return WrappedComponent.displayName || WrappedComponent.name || 'Component';
}

function getContainer(pathname: string) {
    return pathname.split('/')[1];
}

type MapKeysParameters = Parameters<typeof mapKeys>;

type MapKeysData = MapKeysParameters[0];

type MapKeysMap = Map<string, string>;

function mapEventData(data: MapKeysData, map: MapKeysMap) {
    return mapKeys(
        data,
        (_value, propertyKey) => map.get(propertyKey) || propertyKey
    );
}

type OmitParameters = Parameters<typeof omit>;

type OmitProps = OmitParameters[0];

function omitProps(props: OmitProps) {
    return omit(props, ['dispatch', 'history', 'match', 'staticContext']);
}

function dispatchEvent(details: MapKeysData) {
    document.dispatchEvent(
        new CustomEvent('bbc-user-click', {
            detail: mapEventData(details, DETAILS_MAP),
        })
    );
}

type WithEventTrackingLocation = {
    pathname: string;
};

type WithEventTrackingProps<Props extends object> = Props &
    RouteComponentProps & {
        eventData?: MapKeysData;
        location: WithEventTrackingLocation;
    };

type WithEventTrackingComponentCreateEventHandler = (
    eventData: MapKeysData
) => () => void;

type WithEventTrackingComponentCreateEventAttributes = (
    eventData: MapKeysData
) => MapKeysData;

type WithEventTrackingComponent<Props extends object> = ComponentType<
    Props & {
        createEventHandler: WithEventTrackingComponentCreateEventHandler;
        createEventAttributes: WithEventTrackingComponentCreateEventAttributes;
    }
>;

function withEventTracking<Props extends object>(
    Component: WithEventTrackingComponent<Props>
) {
    const WithEventTracking = ({
        location,
        eventData: eventDataFromProps,
        ...restProps
    }: WithEventTrackingProps<Props>) => {
        const defaultData = {
            container: getContainer(location.pathname),
            source: DEFAULT_SOURCE,
            ...eventDataFromProps,
        };

        const createEventHandler: WithEventTrackingComponentCreateEventHandler = eventData => () => {
            dispatchEvent({ ...defaultData, ...eventData });
        };

        const {
            journeyGroupType,
            sequenceId,
            featureToggles: { viewabilityEvents },
        } = useStateContext();

        const containerName = resolvePageForEvent(defaultData.container);

        const createEventAttributes: WithEventTrackingComponentCreateEventAttributes = eventData => {

            const data: Partial<{
                container: string,
                source: string,
                name: string,
                type: string,
                ignoreViews: string
            }> = {...defaultData, ...eventData}; 

            const ignoreViews = data.ignoreViews || '1'; 
            const map = ATTRIBUTES_MAP;

            const mappedAttributes = viewabilityEvents && journeyGroupType
                ? getViewabilityEvent(
                      containerName,
                      location.pathname,
                      ignoreViews,
                      data.name,
                      data.type,
                      journeyGroupType,
                      sequenceId
                  )
                : mapKeys(
                      data,
                      (_value, propertyKey) =>
                          map.get(propertyKey) || propertyKey
                  );

            return mappedAttributes;

        }; 

        return (
            <Component
                {...(omitProps(restProps) as Props)}
                createEventHandler={createEventHandler}
                createEventAttributes={createEventAttributes}
            />
        );
    };

    WithEventTracking.displayName = `WithEventTracking${getDisplayName(
        Component
    )}`;

    return withRouter(WithEventTracking);
}

export { dispatchEvent, eventAttributesShape };

export default withEventTracking;
